import { AdminRail } from '@/components/admin/admin-rail';
import { LoginCard } from '@/components/admin/login-card';
import { AtelierBanner } from '@/components/admin/prod/atelier-banner';
import { VitrineBanner } from '@/components/admin/vitrine/vitrine-banner';
import { VitrineMark } from '@/components/admin/vitrine/vitrine-mark';
import { ADMIN_DOOR_WORDING, adminRefusalWording, signedInAs } from '@/lib/admin-labels';
import { doorForPath } from '@/lib/admin-nav';
import { ADMIN_SESSION_KEY, type AdminSession, AdminSessionProvider } from '@/lib/admin-session';
import { fetchAdminSessionStatus, openAdminSession } from '@/lib/api';
import { ATELIER_INSTANCE, InstanceProvider, useInstance, useIsVitrine } from '@/lib/use-instance';
import { vitrineRedirect } from '@/lib/vitrine';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Navigate,
  Outlet,
  createFileRoute,
  useLinkProps,
  useRouterState,
} from '@tanstack/react-router';
import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react';
import { useCallback, useMemo, useState } from 'react';

/**
 * The shell of the administration screens (tableau de bord spec §4, A1/A3; the
 * account door of auth Tahiti §6; the rail of issue #24).
 *
 * A LAYOUT route: the plugin nests by path prefix, so the files in
 * `routes/admin/` are its children without moving or being renamed. That is the
 * whole point of doing it this way - `/admin/sessions?session=xxx` is meant to be
 * pasted into a team thread (transcript admin §6) and the e2e specs go straight
 * to their URLs. Neither notices this file exists.
 *
 * It owns exactly three things: the session, once for every screen; the
 * navigation; and the width of the page. It deliberately renders NO title -
 * each screen keeps its own `<h1>` and its own opening sentence, which say
 * different things that no shared heading would replace.
 *
 * ## What the door became
 *
 * It used to hold a shared token in `sessionStorage` and decide on its own
 * whether to show the screens. It cannot any more: the credential is an
 * `httpOnly` cookie, invisible to this code by construction. So the shell ASKS
 * - `GET /api/admin/session` - and the three answers are the three states:
 *
 * - **200 with an address**: an operator is connected, named in the bar.
 * - **200 with `null`**: the caller came in by the SHARED TOKEN, which names a
 *   machine and not a person (T6/T8). The screens open, with nobody to name.
 *   This is how the e2e specs and the capture scripts get in - all but the two
 *   that test the door itself, which clear the header to be refused.
 * - **401**: nobody is connected. The door.
 *
 * ## Which server this is (docs/specs/vitrine.md section 7, issue #192)
 *
 * `AdminLayout`, the route's component, first asks `GET /api/instance` and holds
 * the answer in a context for every screen (`lib/use-instance.tsx`); the shell
 * proper, `AdminShell`, is what it always was. It waits for the answer before
 * drawing anything, exactly as the shell waits for the session: a vitrine must
 * not flash the atelier's gestures for the length of a request. On the vitrine
 * the shell paints the mark « Vitrine · v1.3.0 » in the bar and the banner above
 * the screens of a model, and sends the three tabs, the four « nouveau »
 * entries and « État de la prod » that do not exist there back to the screen
 * they belong to (`vitrineRedirect`). On the atelier it paints none of that - and
 * paints, where a prod is linked, the banner of the atelier above everything
 * (`AtelierBanner`, §9.3).
 *
 * ## The rail on the left, and one line left at the top
 *
 * Issue #24 moved the navigation out of the header and into a vertical rail
 * (`admin-rail.tsx`), which folds into a bottom tab bar below 768 px. What is
 * left of the header is ONE line - the brand, and who is connected.
 *
 * The header survives rather than dying with the tabs, and that was decided
 * rather than inherited: the alternative put the identity behind a hover
 * tooltip on the rail's sign-out icon, which is not an answer on a touch
 * device, where no tooltip exists. It also kept `--spacing-admin-bar` alive as
 * a token, so halving the bar was a one-value edit and the ten surfaces that
 * anchor on it stayed correct without a line moving.
 *
 * ## Why this chrome is here and not in `__root.tsx`
 *
 * The root shell centres a 160 px logo above a reading column. That is the
 * person's chrome, and the operator gets another one. The root therefore steps
 * aside under `/admin` (one line, there) and this file paints the whole window
 * - the door included, which nothing else would frame.
 */

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const instance = useInstance();
  if (instance.isPending) return <PendingFrame />;
  // An answer that cannot be read is the atelier (`lib/use-instance.tsx`).
  return (
    <InstanceProvider value={instance.data ?? ATELIER_INSTANCE}>
      <AdminShell />
    </InstanceProvider>
  );
}

function AdminShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const vitrine = useIsVitrine();
  const gone = vitrine ? vitrineRedirect(pathname) : null;
  const queryClient = useQueryClient();

  /**
   * What the door has to say, already worded - or `null` for silence.
   *
   * A string and not the old boolean, because there are now three things to say
   * and they are not the same kind of thing: silence is « on n'a jamais
   * demandé », a refusal is « ce que vous avez tapé n'est pas ça », and an
   * expiry is neither - nobody mistyped anything.
   */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A screen was refused while the session query still holds a success.
   *
   * Kept beside the query rather than written into it: the query's job is to
   * say what the backend answered, and overwriting that with a local guess
   * would make a refetch silently undo the refusal.
   */
  const [rejected, setRejected] = useState(false);

  const status = useQuery({
    queryKey: ADMIN_SESSION_KEY,
    queryFn: fetchAdminSessionStatus,
    // A 401 is an ANSWER here, not a hiccup: retrying it three times would put
    // three seconds of blank screen between an operator and the door.
    retry: false,
  });

  const login = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      openAdminSession(email, password),
    onSuccess: (view) => {
      setRejected(false);
      setNotice(null);
      // Written straight in rather than invalidated: the answer we just got IS
      // the status, and a refetch would put a blank frame between the click and
      // the screen for no new information.
      queryClient.setQueryData(ADMIN_SESSION_KEY, { email: view.email });
    },
    onError: (error) => setNotice(adminRefusalWording(error)),
  });

  // Stable: it is a dependency of the effect inside `useRejectOn401`, and a
  // fresh identity on every render would make that effect loop.
  const onRejected = useCallback(() => {
    setRejected(true);
    setNotice(ADMIN_DOOR_WORDING.expired);
  }, []);

  const email = status.data?.email ?? null;
  const connected = status.isSuccess && !rejected;

  const session = useMemo<AdminSession>(() => ({ email, onRejected }), [email, onRejected]);

  if (status.isPending) {
    /*
      Deliberately NOT the door. The status query answers in a few milliseconds,
      and showing the login form for that one frame before swapping it for the
      screens is the worst of both: an operator starts typing into a form that
      is about to vanish. An empty frame of the right shape is honest.
    */
    return <PendingFrame />;
  }

  if (!connected) {
    // The door names the screen it is about to open, read from the catalogue -
    // which is what keeps the e2e specs clicking the same button names they
    // always did (§6, A4).
    const door = doorForPath(pathname);
    return (
      <DoorScene>
        <LoginCard
          title={door.gateTitle}
          action={door.gateAction}
          notice={notice}
          submitting={login.isPending}
          onSubmit={(entered, password) => login.mutate({ email: entered, password })}
        />
      </DoorScene>
    );
  }

  return (
    <AdminSessionProvider value={session}>
      {/*
        The banner of the atelier (§9.3, lot F): ONE thin line above the whole administration, the rail
        included, in the normal flow and NOT sticky - the sticky surfaces below are pinned to
        `--spacing-admin-bar` from the top of the window, and a second piece of furniture up there
        would move ten of them. It draws nothing on the vitrine, nor where no prod is linked.
      */}
      <AtelierBanner />
      {/*
        `md:flex` and nothing below it: on a phone the rail is `position: fixed`
        along the bottom edge, so it is out of flow and the column is the whole
        window. From 768 px up they are two columns of one row.
      */}
      <div data-admin-shell className="min-h-screen bg-white md:flex">
        {/*
          The rail takes no props: it reads who is connected off the session
          context this provider already carries, and the sign-out gesture off
          `useAdminSignOut` - which is where that mutation went when the button
          left this file (issue #24). One gesture, one implementation, and
          Réglages mounts the same hook for the phone.
        */}
        <AdminRail />
        {/*
          `min-w-0` is load-bearing and not defensive. A flex child defaults to
          `min-width: auto`, which refuses to shrink below its content's
          intrinsic width - it is one of the four causes `admin-responsive.spec`
          spent a lot to close, and a wide table inside `<main>` would push this
          column past the viewport and scroll the body sideways.
        */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/*
            Sticky, and PINNED to `--spacing-admin-bar` (`styles.css`) - the same
            token every sticky surface under `/admin` anchors on (`top-admin-bar`),
            so the height a surface makes room for and the height the bar paints
            cannot drift apart. `admin-bar-offset.test.ts` reads this very tag.

            `z-20`, deliberately BELOW the full-screen overlays: the tester
            preview (`z-30`, its chrome `z-40`), the media picker and the modals
            (`z-40`), the narrow session sheet (`z-50`).
          */}
          <header className="sticky top-0 z-20 h-admin-bar border-gray-200 border-b bg-white">
            <div className="flex h-full items-center gap-3 px-6">
              <BrandLink />
              <VitrineMark />
              <SessionBadge email={email} />
            </div>
          </header>
          {/*
            1180 px, the page width of the maquette. `pb-24` below 768 px rather
            than `pb-20`: the bottom tab bar is 56 px of fixed furniture over the
            end of the page, and a last row of a table under it is a row nobody
            can reach.
          */}
          <main className="mx-auto w-full max-w-[1180px] px-6 pt-7 pb-24 md:pb-20">
            <VitrineBanner />
            {gone === null ? <Outlet /> : <Navigate to={gone} replace />}
          </main>
        </div>
      </div>
    </AdminSessionProvider>
  );
}

/**
 * The grey ground the door stands on (issue #35).
 *
 * It is the scene of the reference card of `words-interface`: `gray-50` edge to
 * edge, one column centred on both axes, 432 px wide. Two levels of white and
 * no more, which is what the charte allows - the card is the second, and the
 * ground is the reason it reads as a card at all.
 *
 * It paints NO logo: the card carries it now, centred, and a brand shown twice
 * on one page reads as two brands.
 *
 * No rail either, deliberately - unchanged from the frame this replaces. A rail
 * beside the door would offer seven destinations to somebody who cannot open
 * any of them, and `admin-shell.spec.ts` holds that property.
 */
function DoorScene({ children }: { children?: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-8">
      <main className="flex w-full max-w-[432px] flex-col items-center">{children}</main>
    </div>
  );
}

/**
 * The frame the shell shows while it is still asking who is connected.
 *
 * It was the door's frame too until issue #35 gave the door a scene of its own,
 * and it is kept WHITE rather than moved onto that grey ground - which is a
 * trade-off rather than an oversight, so here is which way it was made.
 *
 * A signed-out operator sees this for a few milliseconds and then the grey
 * scene: one layout flash before a screen they will spend seconds on. A
 * signed-in operator sees this and then the administration window - white, a
 * logo in the same corner, the same shape - and nothing flashes at all. The
 * cookie outlives the tab, eight people open this every day, so the second path
 * is the common one and it is the one that stays smooth.
 *
 * Painting the grey scene here instead would simply swap which of the two
 * flashes; painting nothing at all would flash white either way. What is not an
 * option is showing the form: an operator would start typing into something
 * about to vanish.
 */
function PendingFrame() {
  return (
    <div className="mx-auto flex min-h-screen max-w-4xl flex-col px-6">
      <header className="py-4">
        <img
          src="/brand/logo-horizontal-transparent.png"
          alt="patrickmathieu singularité"
          width={160}
          height={32}
          className="h-8 w-auto"
        />
      </header>
    </div>
  );
}

/**
 * Who is connected.
 *
 * The way OUT is no longer here - it moved to the rail with issue #24, where
 * the request put it. What is left is the sentence, and `null` is not an
 * absence to hide: it means the caller came in by the shared token, which is a
 * machine (T6). Saying so is what makes the two branches of the guard visible
 * on screen rather than only in a `warn` nobody reads during a capture run.
 *
 * Hidden below `sm`, and now nothing is left beside it. That is a change of
 * kind rather than of degree, and it is the reason the bar had to survive at
 * all: on a phone the identity is not readable here, but the way out is a tap
 * away in Réglages, where S2 put it. Before the rail, hiding this sentence
 * would have hidden the only sign-out there was.
 */
function SessionBadge({ email }: { email: string | null }) {
  return (
    <p className="ml-auto hidden text-gray-600 text-sm sm:block">
      {email === null ? ADMIN_DOOR_WORDING.machineSession : signedInAs(email)}
    </p>
  );
}

/**
 * The brand, and the same treatment the rail gives its own anchors.
 *
 * It points at `/admin/sessions` since §15 renamed the destination, because
 * `/admin` redirects there and a logo that goes through a redirect is a logo
 * that flashes. It followed `/admin/journal` before, and it moves with the
 * address rather than leaning on the redirection that address keeps.
 *
 * `Link` would force `aria-current="page"` on it - it spreads `isActive && …`
 * last, so no prop suppresses it - and Sessions would then carry TWO current
 * links, the logo and the rail entry. A screen reader announces both. Written
 * at length in `components/admin/admin-rail.tsx`.
 */
function BrandLink() {
  const {
    'aria-current': _current,
    'data-status': _status,
    className: _class,
    ...rest
  } = useLinkProps({ to: '/admin/sessions' }) as AnchorHTMLAttributes<HTMLAnchorElement> & {
    ref?: Ref<HTMLAnchorElement> | undefined;
    'data-status'?: string | undefined;
  };
  return (
    <a {...rest} className="no-underline">
      {/*
        Colour logo on white - the default pairing of the charte (§02). Smaller
        than the person's header on purpose: the operator's chrome has to give
        the screen its window.
      */}
      <img
        src="/brand/logo-horizontal-transparent.png"
        alt="patrickmathieu singularité"
        width={140}
        height={28}
        className="h-6 w-auto"
      />
    </a>
  );
}
